import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface OnboardingTourProps {
  onComplete: () => void;
}

const OnboardingTour: React.FC<OnboardingTourProps> = ({ onComplete }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      title: "🎉 Welcome to Templimize AI!",
      description: "You're about to discover the most powerful AI course creation platform for coaches. Let's take a quick tour!",
      action: "Let's Go!",
      highlight: null
    },
    {
      title: "✨ AI Course Creator",
      description: "Create complete courses in minutes! Our AI will generate outlines, lessons, quizzes, and everything you need based on your topic.",
      action: "Next",
      highlight: "create-course",
      route: null
    },
    {
      title: "📧 Email Drip Campaigns",
      description: "Automate your email marketing! Set up sequences that automatically send based on user behavior (signups, upgrades, inactivity). Convert 2% → 20%!",
      action: "Next",
      highlight: "drip-campaigns",
      route: null
    },
    {
      title: "🎯 Sales Funnels",
      description: "Access 6 proven funnel templates with conversion rates and step-by-step flows. From Lead Magnets to High-Ticket offers!",
      action: "Next",
      highlight: "sales-funnels",
      route: null
    },
    {
      title: "🚀 Marketing Generator",
      description: "Generate sales pages, email subject lines, social media posts, and more. Then share everywhere with one click!",
      action: "Next",
      highlight: "marketing",
      route: null
    },
    {
      title: "🎨 Customize Your Brand",
      description: "Set your logo, colors, fonts, and brand voice. Everything you create will match your brand automatically!",
      action: "Next",
      highlight: "brand-settings",
      route: null
    },
    {
      title: "🔗 Integrations",
      description: "Connect with Zapier, Mailchimp, Google Drive, Teachable, and more. Export your courses anywhere!",
      action: "Next",
      highlight: "integrations",
      route: null
    },
    {
      title: "🎓 Ready to Create!",
      description: "That's it! You're ready to build your coaching empire. Start by creating your first AI-powered course!",
      action: "Start Creating →",
      highlight: null,
      route: "/create-course"
    }
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Complete the tour
      if (steps[currentStep].route) {
        navigate(steps[currentStep].route!);
      }
      onComplete();
    }
  };

  const handleSkip = () => {
    onComplete();
  };

  const step = steps[currentStep];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl relative overflow-hidden">
        {/* Progress Bar */}
        <div className="h-2 bg-gray-200">
          <div
            className="h-full bg-gradient-to-r from-purple-600 to-pink-600 transition-all duration-500"
            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
          />
        </div>

        {/* Content */}
        <div className="p-8">
          {/* Step Counter */}
          <div className="text-sm text-gray-500 mb-4">
            Step {currentStep + 1} of {steps.length}
          </div>

          {/* Icon/Emoji */}
          <div className="text-6xl mb-6 text-center">
            {currentStep === 0 && '🎉'}
            {currentStep === 1 && '✨'}
            {currentStep === 2 && '📧'}
            {currentStep === 3 && '🎯'}
            {currentStep === 4 && '🚀'}
            {currentStep === 5 && '🎨'}
            {currentStep === 6 && '🔗'}
            {currentStep === 7 && '🎓'}
          </div>

          {/* Title */}
          <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">
            {step.title}
          </h2>

          {/* Description */}
          <p className="text-lg text-gray-700 mb-8 text-center leading-relaxed">
            {step.description}
          </p>

          {/* Visual Preview (for specific steps) */}
          {currentStep === 1 && (
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4 mb-6 border-2 border-purple-200">
              <div className="text-sm font-semibold text-purple-900 mb-2">Example Topics:</div>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">Life Coaching</span>
                <span className="px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-sm">Business Strategy</span>
                <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm">Fitness Training</span>
                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">Mindfulness</span>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 mb-6 border-2 border-indigo-200">
              <div className="text-sm font-semibold text-indigo-900 mb-2">Trigger Examples:</div>
              <div className="space-y-2 text-sm text-gray-700">
                <div>✉️ Send welcome email when user signs up</div>
                <div>🎯 Send upsell email 3 days after course purchase</div>
                <div>⚠️ Send win-back email if inactive for 7 days</div>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="bg-gradient-to-r from-pink-50 to-rose-50 rounded-lg p-4 mb-6 border-2 border-pink-200">
              <div className="text-sm font-semibold text-pink-900 mb-2">Available Funnels:</div>
              <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
                <div>📚 Lead Magnet → Course</div>
                <div>🎤 Webinar → High-Ticket</div>
                <div>💰 Tripwire → Core Product</div>
                <div>🏆 5-Day Challenge</div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 justify-between items-center">
            <button
              onClick={handleSkip}
              className="text-gray-500 hover:text-gray-700 font-medium transition"
            >
              Skip Tour
            </button>

            <div className="flex gap-3">
              {currentStep > 0 && (
                <button
                  onClick={() => setCurrentStep(currentStep - 1)}
                  className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition"
                >
                  ← Back
                </button>
              )}
              <button
                onClick={handleNext}
                className="px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:shadow-lg transform hover:scale-105 transition"
              >
                {step.action}
              </button>
            </div>
          </div>

          {/* Keyboard Hint */}
          <div className="text-center mt-6 text-sm text-gray-400">
            Press Enter to continue or Esc to skip
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;
